// Copyright (c) 2026 PGNS LLC

import { Link } from '@tanstack/react-router';
import { Skeleton } from '@pgns/ui/skeleton';
import { DataUnavailable } from '@/components/data-unavailable';
import { IncidentEntry } from '@/components/incident-timeline';
import { useStatusData } from '@/hooks/use-status-data';

/**
 * A single incident, addressed by the same id the Atom feed links to — feed
 * entries point at `/incidents/{id}`, so this route is part of that contract.
 */
export function IncidentDetailPage({ incidentId }: { incidentId: string }) {
  const { incidents, loading, error } = useStatusData();
  if (loading) return <Skeleton className="h-40 w-full" />;
  const incident = incidents?.incidents.find((candidate) => candidate.id === incidentId);
  return (
    <div className="space-y-6">
      <Link to="/incidents" className="text-sm text-primary hover:underline">
        &larr; All incidents
      </Link>
      {error !== null && <DataUnavailable stale={incidents !== null} />}
      {incident === undefined ? (
        <p className="text-sm text-muted-foreground">
          {error === null
            ? 'No incident with that id. It may have aged out of the 50 most recent.'
            : 'The incident could not be loaded.'}
        </p>
      ) : (
        <IncidentEntry incident={incident} />
      )}
    </div>
  );
}
